/**
 * Navigation Drawer Component
 * Slide-out navigation with frosted glass overlay
 */

class NavDrawer extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('.nav-drawer__overlay');
    this.panel = this.querySelector('.nav-drawer__panel');
    this.closeBtn = this.querySelector('.nav-drawer__close');
    this.expandableLinks = this.querySelectorAll('.nav-drawer__primary-link--expandable');
    this.countryBtn = this.querySelector('.nav-drawer__country-btn');
    this.newsletterForm = this.querySelector('#nav-drawer-newsletter');

    this.isOpen = false;
    this.isClosing = false;
    this.scrollPosition = 0;
    this.transitionDuration = 450; // Match CSS transition duration

    this.bindEvents();
  }

  bindEvents() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Expandable menu items
    this.expandableLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSubmenu(link);
      });
    });

    // Country selector toggle
    if (this.countryBtn) {
      this.countryBtn.addEventListener('click', () => {
        this.toggleCountrySelector();
      });

      // Close country selector when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.nav-drawer__country-selector')) {
          this.closeCountrySelector();
        }
      });
    }

    // Listen for toggle events from hamburger button
    document.addEventListener('nav-drawer:toggle', () => {
      this.toggle();
    });

    // Newsletter form submission
    if (this.newsletterForm) {
      this.newsletterForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleNewsletterSubmit(e);
      });
    }
  }

  async handleNewsletterSubmit(e) {
    const form = e.target;
    const formData = new FormData(form);
    const submitBtn = form.querySelector('.nav-drawer__newsletter-submit');
    const fieldContainer = document.getElementById('nav-drawer-newsletter-field');
    const successMessage = document.getElementById('nav-drawer-newsletter-success');

    // Disable button while submitting
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = '...';
    }

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      // Show success message regardless (Shopify form handling)
      if (fieldContainer) fieldContainer.hidden = true;
      if (successMessage) successMessage.hidden = false;

    } catch (error) {
      // Still show success (form was likely submitted)
      if (fieldContainer) fieldContainer.hidden = true;
      if (successMessage) successMessage.hidden = false;
    }
  }

  open() {
    // Store scroll position before locking body
    this.scrollPosition = window.pageYOffset;

    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('nav-drawer-open');
    document.body.style.top = `-${this.scrollPosition}px`;

    // Focus management - focus first focusable element
    setTimeout(() => {
      this.closeBtn?.focus();
    }, 100);

    // Dispatch event
    this.dispatchEvent(new CustomEvent('nav-drawer:opened'));
  }

  close() {
    if (this.isClosing) return; // Prevent double-closing

    this.isClosing = true;
    this.isOpen = false;

    // Add closing class for reverse animation
    this.classList.add('nav-drawer--closing');

    // Wait for animation to complete before removing classes
    setTimeout(() => {
      this.setAttribute('aria-hidden', 'true');
      this.classList.remove('nav-drawer--closing');
      document.body.classList.remove('nav-drawer-open');
      document.body.style.top = '';

      // Restore scroll position
      window.scrollTo(0, this.scrollPosition);

      // Close any open submenus
      this.closeAllSubmenus();
      this.closeCountrySelector();

      // Return focus to toggle button
      const toggleBtn = document.querySelector('.nav-drawer-toggle');
      if (toggleBtn) {
        toggleBtn.focus();
      }

      this.isClosing = false;

      // Dispatch event
      this.dispatchEvent(new CustomEvent('nav-drawer:closed'));
    }, this.transitionDuration);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  toggleSubmenu(link) {
    const isExpanded = link.getAttribute('aria-expanded') === 'true';
    const submenuId = link.getAttribute('aria-controls');
    const submenu = document.getElementById(submenuId);

    if (!submenu) return;

    // Close other open submenus
    this.expandableLinks.forEach(otherLink => {
      if (otherLink !== link && otherLink.getAttribute('aria-expanded') === 'true') {
        const otherSubmenuId = otherLink.getAttribute('aria-controls');
        const otherSubmenu = document.getElementById(otherSubmenuId);
        if (otherSubmenu) {
          otherLink.setAttribute('aria-expanded', 'false');
          otherSubmenu.hidden = true;
        }
      }
    });

    // Toggle current submenu
    link.setAttribute('aria-expanded', !isExpanded);
    submenu.hidden = isExpanded;
  }

  closeAllSubmenus() {
    this.expandableLinks.forEach(link => {
      const submenuId = link.getAttribute('aria-controls');
      const submenu = document.getElementById(submenuId);
      if (submenu) {
        link.setAttribute('aria-expanded', 'false');
        submenu.hidden = true;
      }
    });
  }

  toggleCountrySelector() {
    if (!this.countryBtn) return;

    const isExpanded = this.countryBtn.getAttribute('aria-expanded') === 'true';
    const listId = this.countryBtn.getAttribute('aria-controls');
    const list = document.getElementById(listId);

    if (list) {
      this.countryBtn.setAttribute('aria-expanded', !isExpanded);
      list.hidden = isExpanded;
    }
  }

  closeCountrySelector() {
    if (!this.countryBtn) return;

    const listId = this.countryBtn.getAttribute('aria-controls');
    const list = document.getElementById(listId);

    if (list) {
      this.countryBtn.setAttribute('aria-expanded', 'false');
      list.hidden = true;
    }
  }
}

// Define the custom element
customElements.define('nav-drawer', NavDrawer);

/**
 * Initialize hamburger toggle buttons
 */
document.addEventListener('DOMContentLoaded', () => {
  // Add click handlers to all drawer toggle buttons
  document.querySelectorAll('.nav-drawer-toggle').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('nav-drawer:toggle'));
    });
  });
});
